import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LocalTrack } from 'livekit-client';
import { Track } from 'livekit-client';
import { selectLocalParticipant } from 'state/selectors';
import { useIsMounted } from 'ui/hooks/useIsMounted';
import { useUserSettingsContext } from 'contexts';
import { AUDIO_CONSTRAINTS } from 'consts';

// Максимальная длительность записи. По истечении этого времени запись
// автоматически останавливается и переходит в playback.
const MAX_RECORDING_MS = 10_000; // 10 секунд

// Минимальная длительность записи. Клик-стоп раньше этого порога игнорируется.
const MIN_RECORDING_MS = 1_000;

// Кодек записи: webm/opus поддерживается всеми Chromium-based браузерами и Firefox.
const PREFERRED_MIME = 'audio/webm;codecs=opus';
// Для Safari добавил mp4 как fallback.
const FALLBACK_MIME = 'audio/mp4';

function pickMimeType(): string {
    if (typeof MediaRecorder === 'undefined') return PREFERRED_MIME;
    if (MediaRecorder.isTypeSupported(PREFERRED_MIME)) return PREFERRED_MIME;
    if (MediaRecorder.isTypeSupported(FALLBACK_MIME)) return FALLBACK_MIME;
    return '';
}

// Свой getUserMedia для случая, когда LiveKit-трека нет.
// В in-call с живым LiveKit-треком мы НЕ вызываем эту функцию — там делаем clone.mediaStreamTrack,
// чтобы не открывать второй handle к устройству (на Linux ALSA-exclusive это может фейлиться).
const getOwnStream = async (preferredDeviceId?: string): Promise<MediaStream | null> => {
    try {
        const audioConstraints: MediaTrackConstraints = preferredDeviceId
            ? { ...AUDIO_CONSTRAINTS, deviceId: { exact: preferredDeviceId } }
            : { ...AUDIO_CONSTRAINTS };
        return await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    } catch (e) {
        console.warn('useMicTest: getUserMedia failed:', e);
        return null;
    }
};

interface UseMicTestResult {
    isRecording: boolean;
    isPlaying: boolean;
    handleClick: () => void;
}

export function useMicTest(): UseMicTestResult {
    const isMounted = useIsMounted();

    const localParticipant = selectLocalParticipant();
    const microphonePublication = localParticipant?.getTrackPublication(Track.Source.Microphone);
    const track = microphonePublication?.track ?? null;

    const { audioInputDeviceId, audioOutputDeviceId } = useUserSettingsContext();

    const [isRecording, setIsRecording] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    // trackRef — хранит актуальный livekit-трек.
    // Синхронизуем через useLayoutEffect (а не присваиванием в теле рендера), чтобы
    // не было side-effect'а во время рендера: React 18 concurrent rendering может прервать
    // или прогнать рендер дважды, и ref мог бы получить «старое» значение.
    const trackRef = useRef<LocalTrack<Track.Kind> | null>(null);
    useLayoutEffect(() => {
        trackRef.current = track;
    }, [track]);

    // cloneRef — клонированный MediaStreamTrack от LiveKit-трека (in-call path).
    // У клона enabled живёт отдельно от оригинала, поэтому можем читать звук, пока
    // оригинал транслирует тишину в мит (enabled=false).
    const cloneRef = useRef<MediaStreamTrack | null>(null);

    // ownStreamRef — собственный MediaStream от getUserMedia.
    // Стопаем целиком — это единственный наш handle к устройству, после теста его держать незачем.
    const ownStreamRef = useRef<MediaStream | null>(null);

    // recorderRef — текущий MediaRecorder.
    const recorderRef = useRef<MediaRecorder | null>(null);

    // timeoutRef — таймаут для авто-стопа записи по истечении MAX_RECORDING_MS.
    const timeoutRef = useRef<number | null>(null);

    // recordStartRef — timestamp начала записи (Date.now()).
    // Нужен для проверки на минимальную длительность.
    const recordStartRef = useRef<number>(0);

    // audioRef — HTMLAudioElement для воспроизведения записанного blob'а.
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // outputDeviceIdRef — latest-ref выбранного устройства вывода.
    // Через ref, чтобы play() не пересоздавался при смене устройства в настройках
    // (и потом не дёргал лишних useEffect'ов вниз по графу).
    const outputDeviceIdRef = useRef(audioOutputDeviceId);
    outputDeviceIdRef.current = audioOutputDeviceId;

    // objectUrlRef — URL на запись теста.
    const objectUrlRef = useRef<string | null>(null);

    // startingRef — guard от re-entry в record() во время await getOwnStream().
    // isRecording в стейте поднимается только после recorder.start(), поэтому без этого
    // флага повторный клик во время промпта на разрешения запустил бы второй getUserMedia
    // и первый стрим утёк бы без stop()'а.
    const startingRef = useRef(false);

    const cleanup = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (recorderRef.current) {
            if (recorderRef.current.state !== 'inactive') {
                // Зануляем ДО stop(): иначе recorder.stop() триггернет нашу recorder.onstop,
                // которая вызовет play() и создаст audio-ресурсы прямо посреди (или после) cleanup'а.
                recorderRef.current.onstop = null;
                recorderRef.current.ondataavailable = null;
                try {
                    recorderRef.current.stop();
                } catch {
                    console.warn('useMicTest: recorder.stop() threw — recorder, скорее всего, уже не в active state');
                }
            }
            recorderRef.current = null;
        }

        if (cloneRef.current) {
            cloneRef.current.onended = null;
            cloneRef.current.stop();
            cloneRef.current = null;
        }

        if (ownStreamRef.current) {
            ownStreamRef.current.getTracks().forEach((t) => {
                t.onended = null;
                t.stop();
            });
            ownStreamRef.current = null;
        }

        if (audioRef.current) {
            audioRef.current.onended = null;
            audioRef.current.onerror = null;
            audioRef.current.pause();
            audioRef.current.src = '';
            audioRef.current = null;
        }

        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }

        // Перестаём глушить исходящий поток.
        // Если livekit-трек НЕ замьючен, то и мы в конце cleanup'а его размьючиваем.
        if (trackRef.current && !trackRef.current.isMuted) {
            try {
                trackRef.current.mediaStreamTrack.enabled = true;
            } catch {
                console.warn('useMicTest: restore mediaStreamTrack.enabled threw — трек, скорее всего, уже остановлен');
            }
        }

        setIsRecording(false);
        setIsPlaying(false);
    }, []);

    const play = useCallback((blob: Blob) => {
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;

        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = cleanup;
        audio.onerror = cleanup;

        // Маршрутизируем playback на выбранный output. setSinkId — async,
        // но ждать его необязательно: браузер дойдёт до play() уже с зарегистрированным sink'ом
        // (event-loop микротасок), а в худшем случае первые миллисекунды уйдут в дефолт —
        // для теста микрофона это приемлемо.
        const deviceId = outputDeviceIdRef.current;
        const audioWithSink = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
        if (deviceId && typeof audioWithSink.setSinkId === 'function') {
            audioWithSink.setSinkId(deviceId).catch((err: unknown) => {
                console.warn('useMicTest: setSinkId failed:', err);
            });
        }

        audio.play().catch(cleanup);

        setIsRecording(false);
        setIsPlaying(true);
    }, []);

    const stop = useCallback(() => {
        if (Date.now() - recordStartRef.current < MIN_RECORDING_MS) return;

        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
            recorderRef.current.stop();
        }
    }, []);

    const record = useCallback(async () => {
        if (startingRef.current) return;
        startingRef.current = true;

        try {
            const existing = trackRef.current;

            let recordingStream: MediaStream;

            // readyState === 'ended' случается после sleep/wake цикла ОС или unplug устройства.
            if (existing && existing.mediaStreamTrack.readyState !== 'ended') {
                // LiveKit уже держит capture — переезжаем на клон, чтобы не открывать второй handle.
                // Глушим исходящий поток, если пользователь сейчас размьючен. Используем
                // MediaStreamTrack.enabled напрямую в обход livekit.mute(), которая
                // выставляет isMuted=true и эмитит TrackMuted-событие — нам это не нужно,
                // потому что UI у пользователя должен остаться unmuted. enabled=false
                // заставляет outgoing-поток эмиттить DTX-тишину, при этом флаг isMuted
                // у livekit'а не меняется и событие не эмитится.
                if (!existing.isMuted) {
                    existing.mediaStreamTrack.enabled = false;
                }

                const clone = existing.mediaStreamTrack.clone();
                clone.enabled = true;
                cloneRef.current = clone;
                clone.onended = cleanup;
                recordingStream = new MediaStream([clone]);
            } else {
                // LiveKit-трека нет — открываем свой getUserMedia.
                const stream = await getOwnStream(audioInputDeviceId);

                if (!isMounted()) {
                    stream?.getTracks().forEach((t) => t.stop());
                    return;
                }

                if (!stream) {
                    cleanup();
                    return;
                }

                ownStreamRef.current = stream;
                // hot-plug / unplug устройства во время теста — стопаем тест.
                stream.getTracks().forEach((t) => {
                    t.onended = cleanup;
                });
                recordingStream = stream;
            }

            const mimeType = pickMimeType();
            const recorder = mimeType
                ? new MediaRecorder(recordingStream, { mimeType })
                : new MediaRecorder(recordingStream);
            recorderRef.current = recorder;

            const chunks: Blob[] = [];
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.push(e.data);
            };
            recorder.onstop = () => {
                // Запись закончена — отпускаем capture: клон останавливаем, свой стрим стопаем целиком.
                // Держать handle к устройству на время playback'а смысла нет.
                if (cloneRef.current) {
                    cloneRef.current.onended = null;
                    cloneRef.current.stop();
                    cloneRef.current = null;
                }
                if (ownStreamRef.current) {
                    ownStreamRef.current.getTracks().forEach((t) => {
                        t.onended = null;
                        t.stop();
                    });
                    ownStreamRef.current = null;
                }
                recorderRef.current = null;

                const blob = new Blob(chunks, { type: recorder.mimeType });
                play(blob);
            };

            try {
                recorder.start();
            } catch (err) {
                // Трек мог уйти в 'ended' между pre-check'ом и start'ом (sleep/wake race, unplug устройства).
                console.warn('useMicTest: recorder.start() threw — трек, скорее всего, ушёл в ended:', err);
                cleanup();
                return;
            }
            recordStartRef.current = Date.now();
            timeoutRef.current = window.setTimeout(() => {
                timeoutRef.current = null;
                if (recorderRef.current && recorderRef.current.state !== 'inactive') {
                    recorderRef.current.stop();
                }
            }, MAX_RECORDING_MS);

            setIsRecording(true);
        } finally {
            startingRef.current = false;
        }
    }, [audioInputDeviceId]);

    const handleClick = useCallback(() => {
        if (isPlaying) {
            cleanup();
            return;
        }
        if (isRecording) {
            stop();
            return;
        }

        // void чисто для ясности: record - асинхронная функция, но мы не ждём/обрабатываем её результат.
        void record();
    }, [isRecording, isPlaying, record]);

    // Срабатывает, если LiveKit-track исчез во время активного теста через clone-путь
    // (hot-plug устройства, внешний unpublish, livekit-ошибка). Для own-stream пути
    // потеря устройства ловится через t.onended на самих треках.
    useEffect(
        function trackLossDetector() {
            if (cloneRef.current && !track && (isRecording || isPlaying)) cleanup();
        },
        [track, isRecording, isPlaying],
    );

    useEffect(() => {
        return () => cleanup();
    }, []);

    return { isRecording, isPlaying, handleClick };
}
